import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Line,
  LinearFilter,
  LinearMipmapLinearFilter,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  NearestFilter,
  NoToneMapping,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  SRGBColorSpace,
  Texture,
  WebGLRenderer,
} from 'three';
import type { RaceAssets } from '../assets/AssetLoader';
import type { ModelAsset } from '../assets/ModelAsset';
import { sceneRootTransform } from '../assets/TrackVisualManifest';
import type { ViewportDebugMetrics } from '../camera/ViewportDebugOverlay';
import { clampCameraScroll, worldPosition, worldYaw } from './coordinates';
import { probeRgbAlignment } from './RgbAlignmentProbe';
import { createSceneLights } from './sceneLighting';

export interface RenderPose {
  x: number;
  y: number;
  headingRad: number;
}

/** Presentation only: no simulation stepping, input, or network ownership. */
export class ThreeRaceRenderer {
  readonly renderer: WebGLRenderer;
  private readonly scene = new Scene();
  private readonly camera = new OrthographicCamera(-320, 320, 240, -240, 0.1, 2000);
  private readonly vehicle = new Group();
  private readonly resources: Array<{ dispose(): void }> = [];
  private scenery: ModelAsset | null = null;
  private visual = 'landscape';
  private width = 640;
  private height = 480;
  private bounds = { width: 0, height: 0 };
  private zoom = 1;
  private scroll = { x: 0, y: 0 };
  private followStarted = false;
  private disposed = false;
  private lastPose: RenderPose | null = null;
  private trace: Line<BufferGeometry, LineBasicMaterial> | null = null;
  private readonly container: HTMLElement;
  private readonly onContextLost: (event: Event) => void;
  private readonly frameBins = new Uint32Array(1001);
  private frameCount = 0;
  private frameElapsed = 0;
  private maxFrame = 0;
  private alignment = '';

  constructor(container: HTMLElement, onFailure: (message: string) => void) {
    this.container = container;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2', { antialias: false, alpha: false });
    if (!context)
      throw new Error(
        'This game requires WebGL2. Enable hardware acceleration or use a browser and device with WebGL2 support.',
      );
    this.renderer = new WebGLRenderer({ canvas, context, antialias: false, alpha: false });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = NoToneMapping;
    this.renderer.setClearColor(0x0d1735);
    if (import.meta.env.VITE_VIEWPORT_DEBUG === 'true') {
      const info = context.getExtension('WEBGL_debug_renderer_info');
      console.info(
        'Renderer qualification',
        JSON.stringify({
          browser: navigator.userAgent,
          gpu: String(context.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : context.RENDERER)),
          webgl: String(context.getParameter(context.VERSION)),
        }),
      );
    }
    this.camera.up.set(0, 0, -1);
    this.onContextLost = (event) => {
      event.preventDefault();
      onFailure('Graphics context lost. Start a fresh race to continue.');
    };
    canvas.addEventListener('webglcontextlost', this.onContextLost);
    canvas.setAttribute('aria-label', 'Top-down race track');
    container.appendChild(canvas);
    this.resize();
  }

  get maxTextureSize(): number {
    return this.renderer.capabilities.maxTextureSize;
  }

  /** Takes ownership of `assets.scene.model`; it is disposed by `clear()`. */
  load(assets: RaceAssets): void {
    this.clear();
    const { width, height } = assets.track.getSampler();
    this.bounds = { width, height };
    const { scene } = assets;
    this.scenery = scene?.model ?? null;
    for (const image of [assets.landscape, assets.car, ...(scene ? [scene.ground] : [])]) {
      if (
        Math.max(image.naturalWidth, image.naturalHeight) >
        this.renderer.capabilities.maxTextureSize
      ) {
        throw new Error(
          `Track texture exceeds this GPU's ${this.renderer.capabilities.maxTextureSize}px limit. No resizing was applied.`,
        );
      }
    }
    // A scene ground image may be supersampled (2x); it always spans the map extent.
    const ground = scene
      ? this.imagePlane(scene.ground, false, { width, height, smooth: true })
      : this.imagePlane(assets.landscape, false);
    ground.position.set(...worldPosition(width / 2, height / 2));
    this.scene.add(ground);
    if (scene) {
      const { scale, position } = sceneRootTransform(scene.manifest);
      const scenery = new Group();
      scenery.name = 'trackScenery';
      scenery.scale.setScalar(scale);
      scenery.position.set(...position);
      scenery.add(scene.model.root);
      this.scene.add(scenery, createSceneLights(scene.manifest));
      this.visual = `scene v${scene.manifest.visualRevision}`;
    }
    this.vehicle.add(this.imagePlane(assets.car, true));
    this.scene.add(this.vehicle);
    if (import.meta.env.VITE_PHYSICS_PATH_TRACE === 'true') {
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new Float32BufferAttribute(new Float32Array(2048 * 3), 3));
      geometry.setDrawRange(0, 0);
      const material = new LineBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0.75 });
      this.trace = new Line(geometry, material);
      this.trace.frustumCulled = false;
      this.resources.push(geometry, material);
      this.scene.add(this.trace);
    }
    // Upload before the live clock starts, so texture upload cannot consume race ticks.
    for (const resource of this.resources)
      if (resource instanceof Texture) this.renderer.initTexture(resource);
    this.renderer.compile(this.scene, this.camera);
    if (import.meta.env.VITE_VIEWPORT_DEBUG === 'true' && !scene)
      this.alignment = probeRgbAlignment(this.renderer, ground, assets.landscape);
  }

  recordFrame(delta: number): void {
    if (import.meta.env.VITE_VIEWPORT_DEBUG !== 'true' || delta <= 0 || !Number.isFinite(delta))
      return;
    this.frameBins[Math.min(1000, Math.ceil(delta))] += 1;
    this.frameCount += 1;
    this.frameElapsed += delta;
    this.maxFrame = Math.max(this.maxFrame, delta);
  }

  private imagePlane(
    image: HTMLImageElement,
    transparent: boolean,
    options: { width?: number; height?: number; smooth?: boolean } = {},
  ): Mesh<PlaneGeometry, MeshBasicMaterial> {
    const texture = new Texture(image);
    texture.colorSpace = SRGBColorSpace;
    if (options.smooth) {
      // Painted art: filtered and mipmapped. RGB maps/sprites stay pixel-exact.
      texture.minFilter = LinearMipmapLinearFilter;
      texture.magFilter = LinearFilter;
      texture.generateMipmaps = true;
      texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    } else {
      texture.minFilter = NearestFilter;
      texture.magFilter = NearestFilter;
      texture.generateMipmaps = false;
    }
    texture.needsUpdate = true;
    const geometry = new PlaneGeometry(
      options.width ?? image.naturalWidth,
      options.height ?? image.naturalHeight,
    );
    const material = new MeshBasicMaterial({
      map: texture,
      transparent,
      toneMapped: false,
      depthWrite: !transparent,
    });
    const mesh = new Mesh(geometry, material);
    // Local +Y (image north / UV top) becomes world -Z. Image top-left is (0,0).
    mesh.rotation.x = -Math.PI / 2;
    this.resources.push(texture, geometry, material);
    return mesh;
  }

  setZoom(zoom: number): void {
    this.zoom = zoom;
  }

  resize(): { width: number; height: number } {
    this.width = Math.max(1, this.container.clientWidth);
    this.height = Math.max(1, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(this.width, this.height);
    return this.getSize();
  }

  getSize(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  present(
    pose: RenderPose,
    advanceFollow = true,
    trace?: ReadonlyArray<{ x: number; y: number }>,
  ): void {
    if (this.disposed) return;
    if (this.trace && trace) {
      const positions = this.trace.geometry.getAttribute('position');
      const count = Math.min(trace.length, positions.count);
      for (let index = 0; index < count; index += 1) {
        const point = trace[trace.length - count + index];
        positions.setXYZ(index, point.x, 0.5, point.y);
      }
      positions.needsUpdate = true;
      this.trace.geometry.setDrawRange(0, count);
    }
    this.lastPose = { ...pose };
    this.vehicle.position.set(...worldPosition(pose.x, pose.y, 1));
    this.vehicle.rotation.y = worldYaw(pose.headingRad);
    if (!this.followStarted) {
      this.scroll = { x: pose.x - this.width / 2, y: pose.y - this.height / 2 };
      this.followStarted = true;
    } else if (advanceFollow) {
      // Phaser 3.90 Camera.preRender: per-render lerp, floor scroll, then bounds.
      this.scroll.x = Math.floor(this.scroll.x + (pose.x - this.width / 2 - this.scroll.x) * 0.18);
      this.scroll.y = Math.floor(this.scroll.y + (pose.y - this.height / 2 - this.scroll.y) * 0.18);
    }
    this.scroll.x = clampCameraScroll(this.scroll.x, this.width, this.bounds.width, this.zoom);
    this.scroll.y = clampCameraScroll(this.scroll.y, this.height, this.bounds.height, this.zoom);
    const x = this.scroll.x + this.width / 2;
    const y = this.scroll.y + this.height / 2;
    this.camera.left = -this.width / 2;
    this.camera.right = this.width / 2;
    this.camera.top = this.height / 2;
    this.camera.bottom = -this.height / 2;
    this.camera.zoom = this.zoom;
    this.camera.position.set(x, 1000, y);
    this.camera.lookAt(x, 0, y);
    this.camera.updateProjectionMatrix();
    this.renderer.render(this.scene, this.camera);
  }

  redraw(): void {
    if (this.lastPose) this.present(this.lastPose, false);
  }

  getMetrics(): ViewportDebugMetrics {
    let p95 = 0;
    let count = 0;
    while (p95 < 1000 && count + this.frameBins[p95] < this.frameCount * 0.95) {
      count += this.frameBins[p95];
      p95 += 1;
    }
    return {
      cssWidth: this.width,
      cssHeight: this.height,
      scaleWidth: this.width,
      scaleHeight: this.height,
      worldX: this.scroll.x + this.width / 2 - this.width / this.zoom / 2,
      worldY: this.scroll.y + this.height / 2 - this.height / this.zoom / 2,
      worldWidth: this.width / this.zoom,
      worldHeight: this.height / this.zoom,
      devicePixelRatio: this.renderer.getPixelRatio(),
      zoom: this.zoom,
      gpu: {
        bufferWidth: this.renderer.domElement.width,
        bufferHeight: this.renderer.domElement.height,
        maxTextureSize: this.renderer.capabilities.maxTextureSize,
        textures: this.renderer.info.memory.textures,
        geometries: this.renderer.info.memory.geometries,
        drawCalls: this.renderer.info.render.calls,
      },
      diagnostics: {
        visual: this.visual,
        alignment: this.alignment,
        frames: this.frameCount,
        elapsedSeconds: this.frameElapsed / 1000,
        p95Ms: p95,
        maxMs: this.maxFrame,
      },
    };
  }

  clear(): void {
    this.lastPose = null;
    this.trace = null;
    this.scene.clear();
    this.vehicle.clear();
    for (const resource of this.resources) resource.dispose();
    this.resources.length = 0;
    this.scenery?.dispose();
    this.scenery = null;
    this.visual = 'landscape';
    this.followStarted = false;
    this.frameBins.fill(0);
    this.frameCount = this.frameElapsed = this.maxFrame = 0;
    this.alignment = '';
    this.renderer.clear();
  }

  dispose(): void {
    if (this.disposed) return;
    this.clear();
    this.disposed = true;
    this.renderer.domElement.removeEventListener('webglcontextlost', this.onContextLost);
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.renderer.domElement.remove();
  }
}
